import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Link, useLocation, useParams } from "react-router-dom";
import {
  applicationStatus,
  fetchMyOrganizerApplication,
  isApproved,
  isSuperadmin,
} from "./organizerApplication.js";
import {
  dashboardEventIdFromPath,
  rememberActiveEventId,
  readRememberedEventId,
  issueTicketPath,
  offlineBookingsPath,
  dashboardPath,
} from "./eventContext.js";
import OrganizerConsoleHeader from "./OrganizerConsoleHeader.jsx";

function NavItem({ to, end, state, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      state={state}
      className={({ isActive }) =>
        [
          "flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition",
          isActive
            ? "bg-amber-100 text-slate-900 shadow-sm"
            : "text-slate-600 hover:bg-amber-50",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

export default function OrganizerLayout() {
  const { pathname } = useLocation();
  const { id: routeId } = useParams();
  const eventId =
    dashboardEventIdFromPath(pathname) ||
    (pathname.startsWith("/events/dashboard/") ? routeId : "") ||
    readRememberedEventId() ||
    "";
  const issueTicketTo = issueTicketPath(eventId);
  const dashboardTo = dashboardPath(eventId);
  const [mobileNav, setMobileNav] = useState(false);
  const [orgStatus, setOrgStatus] = useState({ loading: true, app: null });

  useEffect(() => {
    if (eventId) rememberActiveEventId(eventId);
  }, [eventId]);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("bb_user") || "null");
    } catch {
      return null;
    }
  }, []);

  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";
  const superadmin = isSuperadmin();
  const approved = superadmin || isApproved(orgStatus.app);
  const status = applicationStatus(orgStatus.app);
  const newEventTo = approved ? "/listyourshow/new" : "/listyourshow/account-setup";

  useEffect(() => {
    setMobileNav(false);
  }, [pathname]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    if (mobileNav) document.body.style.overflow = "hidden";
    else document.body.style.overflow = prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [mobileNav]);

  useEffect(() => {
    if (superadmin) {
      setOrgStatus({ loading: false, app: null });
      return undefined;
    }
    let active = true;
    fetchMyOrganizerApplication()
      .then((app) => {
        if (active) setOrgStatus({ loading: false, app });
      })
      .catch((error) => {
        if (active) setOrgStatus({ loading: false, app: error?.status === 404 ? null : null });
      });
    return () => {
      active = false;
    };
  }, [superadmin]);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <OrganizerConsoleHeader organizerId={orgStatus.app?.organizerId || user?.organizerId || ""} />
      <div className="mx-auto max-w-7xl flex flex-col lg:flex-row gap-0 lg:gap-6 px-3 sm:px-4 py-4">
        {/* Mobile bar */}
        <div className="lg:hidden flex items-center justify-between gap-2 mb-3 rounded-xl border border-amber-100 bg-white px-3 py-2 shadow-sm">
          <span className="text-sm font-semibold text-slate-800">List Your Show</span>
          <button
            type="button"
            className="rounded-lg border px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-amber-50"
            onClick={() => setMobileNav((v) => !v)}
            aria-expanded={mobileNav}
          >
            Menu
          </button>
        </div>

        {/* Sidebar */}
        <aside
          className={[
            "lg:w-56 shrink-0 rounded-xl border border-amber-100 bg-white p-3 shadow-sm h-fit lg:sticky lg:top-20",
            mobileNav ? "block" : "hidden lg:block",
          ].join(" ")}
          aria-label="Organizer navigation"
        >
          <div className="px-1 pb-2 mb-2 border-b border-slate-100">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-800/90">
              List Your Show
            </div>
            <p className="text-xs text-slate-500 mt-0.5 leading-snug">
              Create a listing, follow its review status and manage approved events.
            </p>
          </div>
          <nav className="flex flex-col gap-1">
            <NavItem to="/listyourshow/manage" end>
              My events
            </NavItem>
            <NavItem to={newEventTo}>
              {approved ? "New event" : "Account setup"}
            </NavItem>
            {(approved || eventId) && (
              <>
                {eventId ? (
                  <NavItem to={dashboardTo} end state={{ eventId }}>
                    Dashboard
                  </NavItem>
                ) : null}
                <NavItem to={issueTicketTo} state={eventId ? { eventId } : undefined}>
                  Issue ticket
                </NavItem>
                <NavItem to={offlineBookingsPath(eventId)} state={eventId ? { eventId } : undefined}>
                  Offline bookings
                </NavItem>
              </>
            )}
            {!superadmin && (
              <NavItem to="/listyourshow/account-setup">
                Verification
                {!orgStatus.loading && status !== "approved" ? (
                  <span
                    className={[
                      "ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase",
                      status === "pending"
                        ? "bg-amber-100 text-amber-900"
                        : status === "rejected"
                          ? "bg-rose-100 text-rose-800"
                          : "bg-slate-100 text-slate-600",
                    ].join(" ")}
                  >
                    {status === "pending" ? "Pending" : status === "rejected" ? "Action" : "Setup"}
                  </span>
                ) : null}
              </NavItem>
            )}
            <Link
              to="/"
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-amber-50"
            >
              ← Public website
            </Link>
            {isAdmin && (
              <NavLink
                to="/admin"
                className={({ isActive }) =>
                  [
                    "rounded-lg px-3 py-2 text-sm font-medium transition",
                    isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100",
                  ].join(" ")
                }
              >
                {role === "superadmin" ? "Global admin panel" : "My events admin panel"}
              </NavLink>
            )}
          </nav>
        </aside>

        {/* Main */}
        <div className="flex-1 min-w-0 rounded-xl border border-amber-100/80 bg-white shadow-sm p-4 sm:p-5 md:p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
